// @ts-strict-ignore
import { getConfiguratorViewInfo } from '@configurator/view/configurator-view-info';
import { MENU_SEPARATOR, MenuInterface, MenuItem, MenuList } from './menu.interfaces';
import { UserModel } from '@user/model/user.model';
import { ProjectUtils } from '../../components/project/util/project-utils';
import { ProjectModel } from '../../model/project.model';
import { UserUtils } from '@user/service/user-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { Hotkey, HotkeysService } from 'angular2-hotkeys';
import { Injectable } from '@angular/core';
import { EventUtils } from '@shared/util/event-utils';
import { SaveProjectService } from '@project/components/project/project-save/save-project.service';
import { UserActionsLoggingManager } from '@user/service/logging/user-actions-logging.manager';
import { ProjectOptionUtils } from '../../components/project/util/project-option.utils';
import { TicketService } from '@shared/service/ticket.service';
import { CheckSaveProjectService } from '../check-save-project.service';
import { Subject } from 'rxjs/internal/Subject';
import { ProjectValidationService } from '../../components/validation/service/project-validation.service';
import { ApplicationService } from '@shared/application/application.service';
import { ElbridgeService } from '../../components/project/elbridge/service/elbridge.service';
import { ProjectNavigationPresenter } from '@project-navigation/project-navigation.presenter';
import { ProjectService } from '../../components/import-export/service/project.service';
import { ProjectComponentPresenter } from '../../components/project-component.presenter';
import { ConfigurationCreationWizardPresenter } from '@configuration/wizard/presenter/configuration-creation-wizard.presenter';
import { OrderMenuCreator } from './order-menu.creator';
import { Observable } from 'rxjs';
import { MountingMenuCreator } from './mounting-menu.creator';
import { UserPermissionService } from '@user/service/user-permission.service';
import { InterfaceMenuCreator } from './interface-menu.creator';
import { InternalMenuCreator } from './internal-menu-creator';
import { DocumentationMenuCreator } from './documentation-menu.creator';

export type ProjectMenuItemName =
  | 'examineProject';

@Injectable({providedIn: 'root'})
export class MenuService {

  private _menuChangedSubject = new Subject<void>();

  constructor(
    private hotkeysService: HotkeysService,
    private userActionsLoggingManager: UserActionsLoggingManager,
    private userModel: UserModel,
    private projectModel: ProjectModel,
    private projectNavigationPresenter: ProjectNavigationPresenter,
    private saveProjectService: SaveProjectService,
    private localeResourceManager: LocaleResourceManager,
    private ticketService: TicketService,
    private projectService: ProjectService,
    private checkSaveProjectService: CheckSaveProjectService,
    private projectValidationService: ProjectValidationService,
    private applicationService: ApplicationService,
    elbridgeService: ElbridgeService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private configurationCreationWizardPresenter: ConfigurationCreationWizardPresenter,
    private userPermissionService: UserPermissionService,
    private interfaceMenuCreator: InterfaceMenuCreator,
    private mountingMenuCreator: MountingMenuCreator,
    private documentationMenuCreator: DocumentationMenuCreator,
    private orderMenuCreator: OrderMenuCreator,
    private internalMenuCreator: InternalMenuCreator,
  ) {
    elbridgeService.elbridgeHookChanged$.subscribe(() => this.notifyMenuChanged());
    interfaceMenuCreator.menuChanged$.subscribe(() => this.notifyMenuChanged());
  }

  public get menuChanged$(): Observable<void> {
    return this._menuChangedSubject.asObservable();
  }

  public getMenu(): MenuInterface {
    const menu: MenuInterface = {};
    menu['header.menu.project'] = {id: 'header-menu-project', content: this.getProjectItems()};
    menu['header.menu.interfaces'] = this.interfaceMenuCreator.create();
    menu['header.menu.order'] = this.orderMenuCreator.create();
    menu['header.menu.mounting'] = this.mountingMenuCreator.create();
    menu['header.menu.documentation'] = this.documentationMenuCreator.create();
    menu['header.menu.internal'] = this.internalMenuCreator.create();
    Object.keys(menu).forEach((key: string) => {
      if (!menu[key].content.filter((item) => (<MenuItem>item).visible).length) {
        delete menu[key];
      }
    });
    return menu;
  }

  public runLoggedAction(item: MenuItem) {
    this.userActionsLoggingManager.logFunction(item.appActionLogId);
    item.action();
  }

  private get itemVisibility() {
    return getConfiguratorViewInfo(this.projectModel.currentProjectType).menu;
  }

  private getProjectItems(): MenuList {
    const items: MenuList = [
      {
        label: 'header.menu.new_configuration',
        action: () => this.openNewConfigurationDialog(),
        // shortcut: 'Strg/Ctrl+N',
        enabled: true,
        visible: this.userModel.checkPermissions(["EDIT_PROJECT"]),
        appActionLogId: 'header.menu.new_configuration',
        id: 'header-menu-project-new-configuration',
      },
      {
        label: 'header.menu.open',
        action: () => this.projectNavigationPresenter.openLoadProjectDialog(),
        shortcut: 'ctrl+o',
        enabled: true,
        visible: this.isOpenVisible(),
        appActionLogId: 'header.menu.open',
        id: 'header-menu-project-open',
      },
      MENU_SEPARATOR,
      {
        label: 'header.menu.save',
        action: () => this.saveProjectService.saveCurrentProject(),
        shortcut: 'ctrl+s',
        enabled: this.saveProjectService.isSaveCurrentProjectEnabled(),
        visible: this.isSaveAsVisible(),
        appActionLogId: 'header.menu.save',
        id: 'header-menu-project-save',
      },
      {
        label: 'header.menu.save_as',
        action: () => this.saveProjectService.saveCurrentProjectAs(),
        shortcut: 'ctrl+shift+s',
        enabled: this.isSaveAsEnabled(),
        visible: this.isSaveAsVisible(),
        appActionLogId: 'header.menu.save_as',
        id: 'header-menu-project-save-as',
      },
      MENU_SEPARATOR,
      {
        label: 'header.menu.local_backup',
        action: () => this.actionWithWsp(),
        enabled: true,
        visible: this.isActionWithWspVisible(),
        appActionLogId: 'header.menu.local_backup',
        id: 'header-menu-import-wsp',
      },
      MENU_SEPARATOR,
      {
        label: 'header.menu.examine_project',
        action: () => this.projectValidationService.examineProject(),
        // shortcut: 'Umschalt+Strg+P',
        enabled: this.projectValidationService.isExamineProjectEnabled(),
        visible: this.itemVisibility.projectMenuItemsVisibility.examineProject,
        appActionLogId: 'header.menu.examine_project',
        id: 'header-menu-project-check-config',
      },
      MENU_SEPARATOR,
      {
        label: 'header.menu.edit_properties',
        action: () => this.openModalSettingsProjectDialog(),
        // shortcut: 'Strg+,',
        enabled: true,
        visible:
          this.projectModel.currentProjectType &&
          !!this.projectModel.currentProject.projectGroup &&
          this.userModel.checkPermissions(["LOAD_PROJECT_OPTION_DEFINITIONS"]) &&
          !ProjectUtils.isNewWSPImportedProject(this.projectModel.currentProject) &&
          !this.projectModel.isProjectOpenedByTemporaryLink,
        appActionLogId: 'header.menu.edit_properties',
        id: 'header-menu-project-edit-properties',
      },
      {
        label: 'header.menu.edit_config_properties',
        action: () => this.projectComponentPresenter.openEditConfigurationSettingsByUser(),
        enabled: true,
        visible: this.isConfigurationSettingsAvailable(),
        appActionLogId: 'header.menu.edit_config_properties',
        id: 'header-menu-project-edit-config-properties',
      },
      MENU_SEPARATOR,
      {
        label: 'shareProjectPopup.title',
        action: () => this.openPermissionDialog(),
        enabled: true,
        visible: this.isShareProjectsVisible(),
        appActionLogId: 'shareProjectPopup.title',
        id: 'header-menu-project-permissions',
      },
      {
        label: 'header.menu.share',
        action: () => this.shareProjectLink(),
        enabled: this.isShareEnabled(),
        visible: !this.userModel.isConstruction,
        appActionLogId: 'header.menu.share',
        id: 'header-menu-project-share',
      }];
    this.addMenuItemShortCuts(items);
    return items;
  }

  public getInfoItems(): MenuList {
    const items: MenuList = [
      {
        label: 'header.menu.faq',
        action: () => this.openFAQLink(),
        enabled: true,
        visible: true,
        appActionLogId: 'header.menu.faq',
        id: 'header-menu-help-faq',
      },
      {
        label: 'header.menu.release_notes',
        action: () => this.openFAQLink_version(),
        enabled: true,
        visible: true,
        appActionLogId: 'header.menu.release_notes',
        id: 'header-menu-help-release-notes',
      },
      {
        label: 'header.menu.technical_notes',
        action: () => this.openTechnicalNotesLink(),
        enabled: true,
        visible: true,
        appActionLogId: 'header.menu.technical_notes',
        id: 'header-menu-help-technical-notes',
      },
      {
        label: 'header.menu.tolerance_table',
        action: () => this.openToleranceTableLink(),
        enabled: this.isToleranceTableEnabled(),
        visible: this.isToleranceTableEnabled(),
        appActionLogId: 'header.menu.tolerance_table',
        id: 'header-menu-help-tolerances',
      },
      {
        label: 'header.menu.wago_address',
        action: () => this.openWagoComLink(),
        enabled: true,
        visible: true,
        appActionLogId: 'header.menu.wago_address',
        id: 'header-menu-help-wago',
      },
      {
        label: 'header.menu.support',
        action: () => this.openSupportLink(),
        enabled: true,
        visible: true,
        appActionLogId: 'header.menu.support',
        id: 'header-menu-help-support',
      },
      {
        label: 'header.menu.imprint',
        action: () => this.openImprintLink(),
        enabled: true,
        visible: true,
        appActionLogId: 'header.menu.imprint',
        id: 'header-menu-help-imprint',
      },
      {
        label: 'header.menu.info_internal',
        action: () => this.openFAQLink_internal(),
        enabled: this.isWagoUser(),
        visible: this.isWagoUser(),
        appActionLogId: 'header.menu.info_internal',
        id: 'header-menu-help-internal',
      }
      , MENU_SEPARATOR,
      {
        label: this.localeResourceManager.getValueByKey('header.menu.version', {'0': this.applicationService.applicationVersion}),
        action: () => this.openFAQLink_version(),
        enabled: true,
        visible: true,
        appActionLogId: 'header.menu.version',
        id: 'header-menu-help-version',
      }];
    this.addMenuItemShortCuts(items);
    return items;
  }

  private addMenuItemShortCuts(items: MenuList) {
    for (const item of items) {
      if (item == MENU_SEPARATOR || !item.shortcut) continue;
      this.hotkeysService.add(
        new Hotkey(item.shortcut, () => {
          if (item.visible && item.enabled) {
            this.userActionsLoggingManager.logFunction(item.appActionLogId);
            item.action();
            return false;
          }
        })
      );
    }
  }

  private isSaveAsEnabled() {
    return !ProjectUtils.isEmptyProject(this.projectModel.currentProject) && !this.projectModel.isProjectOpenedByTemporaryLink;
  }

  private isSaveAsVisible() {
    return (
      this.userModel.checkPermissions(["SAVE_PROJECTS", "GET_LIST_OF_PROJECTS"]) &&
      !this.projectModel.isProjectOpenedByTemporaryLink
    );
  }

  private isActionWithWspVisible() {
    return getConfiguratorViewInfo(this.projectModel.currentProjectType).menu.interfaceMenuItemsVisibility['importExportWsp']
      && this.userPermissionService.isLocalBackupVisible();
  }

  private actionWithWsp(): void {
    this.projectComponentPresenter.openPopupActionWithWsp();
  }

  protected async openNewConfigurationDialog() {
    if (await this.checkSaveProjectService.checkSaveProjectOrAbort()) {
      return this.configurationCreationWizardPresenter.openWizard(false);
    }
  }

  private openFAQLink(): void {
    window.open(this.localeResourceManager.getValueByKey(`link.faq`));
  }

  private openFAQLink_internal(): void {
    window.open(this.localeResourceManager.getValueByKey(`link.faq_internal`));
  }

  private openFAQLink_version(): void {
    window.open(this.localeResourceManager.getValueByKey(`link.faq_version`));
  }

  private openTechnicalNotesLink(): void {
    window.open(this.localeResourceManager.getValueByKey(`link.technical_notes`));
  }

  private openWagoComLink(): void {
    window.open(this.localeResourceManager.getValueByKey(`link.wago_com`), `_blank`);
  }

  private openSupportLink(): void {
    this.ticketService.getSupportEmailTicketId().subscribe((ticketId: number) => {
      EventUtils.resetBeforeUnload();
      window.open(this.localeResourceManager.getValueByKey(`link.support`, {'0': ticketId || ''}), `_self`).focus();
      setTimeout(() => {
        EventUtils.restoreBeforeUnload();
      }, 0);
    });
  }

  private openImprintLink(): void {
    window.open(this.localeResourceManager.getValueByKey(`link.imprint`), `_blank`);
  }

  private isShareEnabled(): boolean {
    return !this.projectModel.isNewConfiguration && !this.projectModel.isProjectOpenedByTemporaryLink;
  }

  private shareProjectLink(): void {
    this.projectComponentPresenter.openShareProjectLinkPopup(
      this.projectModel.currentProject.id,
      this.projectModel.currentProject.currentVersion,
      this.projectModel.currentProject.modificationDateServerTimeStamp,
      this.projectModel.currentProject.articleNumberLong.reducedArticleNumberLong,
      this.projectService.getProjectName(this.projectModel.currentProject)
    );
  }

  private isOpenVisible() {
    return this.userModel.checkPermissions(["LOAD_PROJECTS", "GET_LIST_OF_PROJECTS"]) && this.userPermissionService.isLoggedIn();
  }

  private openPermissionDialog(): void {
    this.projectComponentPresenter.openPermissionDialog();
  }

  private openModalSettingsProjectDialog(): void {
    this.projectComponentPresenter.openEditProjectSettingsComponent(this.projectModel.configurationGroup, this.projectModel.currentProject.isArticleProject);
  }

  private isShareProjectsVisible(): boolean {
    return this.userModel.user && this.userModel.user.hasPermission("GET_LIST_OF_FRIENDS")
      && UserUtils.canHaveFriend(this.userModel.user);
  }

  private notifyMenuChanged(): void {
    this._menuChangedSubject.next(undefined);
  }

  private isConfigurationSettingsAvailable(): boolean {
    return (
      ProjectOptionUtils.isProjectSettingsAvailable(this.projectModel.currentProject, this.projectModel.user) &&
      !this.projectModel.isNewConfiguration &&
      !this.projectModel.isProjectOpenedByTemporaryLink
    );
  }

  private isWagoUser(): boolean {
    return UserUtils.isWagoUser(this.userModel.user);
  }

  private isToleranceTableEnabled(): boolean {
    return this.projectModel.currentProjectType === "MOUNTING_RAIL";
  }

  private openToleranceTableLink(): void {
    window.open(this.localeResourceManager.getValueByKey(`link.tolerance_table`));
  }

  public setHotkeysAvailability(isAvailable: boolean): void {
    if (isAvailable) {
      this.hotkeysService.unpause();
    } else {
      this.hotkeysService.pause();
    }
  }
}
